import { useEffect, useMemo, useState } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { CalendarCheck, Clock } from 'phosphor-react';
import {
  Supplier,
  getOrdinalSuffix,
  hasCurrentDueWaiverCutoff,
  NO_CURRENT_DUE_WAIVER_CUTOFF,
} from '@dill/dill-shared';
import { twMerge } from 'tailwind-merge';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import { updateSupplierFeeWaiverSettings } from '../../../../../../redux/services/suppliersService';
import { getUserDetails } from '../../../../../../redux/services/authService';
import { openMessageModal } from '../../../../../../redux/globalSlices/genericSlice';
import { openDillToast } from '../../../../../../utils/helpers';
import { COLORS } from '../../../../../../utils/colors';
import { DillButton, DillSelect, DillToggle } from '../../../../../general/Dill';
import { AppRadioButton } from '../../../../../general/AppRadioButton/AppRadioButton';

type WaiverToggleField = 'waiveForCurrentDuePayments' | 'waiveForDiscountDatePayments';

type WaiverField = WaiverToggleField | 'currentDueWaiverDayOfMonthCutoff';

const WAIVER_META: Record<
  WaiverToggleField,
  { label: string; description: string; Icon: typeof Clock }
> = {
  waiveForCurrentDuePayments: {
    label: 'Waive fees for current due invoices',
    description: 'Credit card fees are waived when every invoice being paid is not past due',
    Icon: Clock,
  },
  waiveForDiscountDatePayments: {
    label: 'Waive fees for discount date invoices',
    description: 'Credit card fees are waived when every invoice is paid within its discount date',
    Icon: CalendarCheck,
  },
};

const ALL_WAIVER_TOGGLE_FIELDS = Object.keys(WAIVER_META) as WaiverToggleField[];

const ALL_WAIVER_FIELDS: WaiverField[] = [
  ...ALL_WAIVER_TOGGLE_FIELDS,
  'currentDueWaiverDayOfMonthCutoff',
];

const CUTOFF_LABEL = 'deadline for the current due waiver';

/** Prox terms most commonly land on the 10th, so that is what the toggle starts at. */
const DEFAULT_WAIVER_DAY_OF_MONTH = 10;

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, i) => ({
  label: getOrdinalSuffix(i + 1),
  value: `${i + 1}`,
}));

type CreditCardFeeWaiverSettings = {
  waiveForCurrentDuePayments: boolean;
  waiveForDiscountDatePayments: boolean;
  /** null means the current due waiver has no deadline. Only read when that waiver is on. */
  currentDueWaiverDayOfMonthCutoff: number | null;
};

interface CreditCardFeeWaiverSettingsSectionProps {
  supplier?: Supplier | null;
}

const getInitialSettings = (supplier?: Supplier | null): CreditCardFeeWaiverSettings => {
  // Suppliers that predate this feature have no cutoff key at all; both it and the sentinel
  // read as "no deadline", which is what null means in local state.
  const storedCutoff = supplier?.creditCardFeeWaiverConditions?.currentDueWaiverDayOfMonthCutoff;
  return {
    waiveForCurrentDuePayments:
      supplier?.creditCardFeeWaiverConditions?.waiveForCurrentDuePayments ?? false,
    waiveForDiscountDatePayments:
      supplier?.creditCardFeeWaiverConditions?.waiveForDiscountDatePayments ?? false,
    currentDueWaiverDayOfMonthCutoff: hasCurrentDueWaiverCutoff(storedCutoff) ? storedCutoff : null,
  };
};

export const CreditCardFeeWaiverSettingsSection = ({
  supplier,
}: CreditCardFeeWaiverSettingsSectionProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);

  const canEdit = useMemo(() => {
    if (!currentUser?.email || !supplier) return false;
    const email = currentUser.email.toLowerCase();
    const supplierEmail = supplier.email?.toLowerCase() ?? '';
    const adminEmails = (supplier.adminSupplierEmails ?? []).map((e) => e.toLowerCase());
    return (
      email === supplierEmail ||
      adminEmails.includes(email) ||
      (currentUser.roles?.includes('DILL_ADMIN') ?? false)
    );
  }, [currentUser, supplier]);

  const [settings, setSettings] = useState<CreditCardFeeWaiverSettings>(() =>
    getInitialSettings(supplier)
  );
  const [initialSettings, setInitialSettings] = useState<CreditCardFeeWaiverSettings>(() =>
    getInitialSettings(supplier)
  );
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const newSettings = getInitialSettings(supplier);
    setSettings(newSettings);
    setInitialSettings(newSettings);
  }, [supplier]);

  const changedFields = useMemo(
    () => ALL_WAIVER_FIELDS.filter((field) => settings[field] !== initialSettings[field]),
    [settings, initialSettings]
  );

  const settingsChanged = changedFields.length > 0;

  const toggleField = (field: WaiverToggleField) => {
    setSettings((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const toggleCutoff = () => {
    setSettings((prev) => ({
      ...prev,
      currentDueWaiverDayOfMonthCutoff:
        prev.currentDueWaiverDayOfMonthCutoff === null ? DEFAULT_WAIVER_DAY_OF_MONTH : null,
    }));
  };

  const performSave = async () => {
    // Send only what changed so untouched flags are never pushed down to buyers. The cutoff goes
    // out as the sentinel rather than null so buyers receive an explicit "no deadline" instead of
    // a value that reads back as "inherit".
    const changedSettings = changedFields.reduce<Partial<CreditCardFeeWaiverSettings>>(
      (acc, field) => ({
        ...acc,
        [field]:
          field === 'currentDueWaiverDayOfMonthCutoff'
            ? settings.currentDueWaiverDayOfMonthCutoff ?? NO_CURRENT_DUE_WAIVER_CUTOFF
            : settings[field],
      }),
      {}
    );

    setSaving(true);
    const result = await dispatch(
      updateSupplierFeeWaiverSettings({
        supplierId: supplier?.id || '',
        creditCardFeeWaiverConditions: changedSettings,
        overwriteExisting,
      })
    );
    setSaving(false);

    if (result.type === 'supplier/updateFeeWaiverSettings/fulfilled') {
      setInitialSettings({ ...settings });
      await dispatch(getUserDetails());
      openDillToast({
        type: 'SUCCESS',
        message: 'Fee waiver settings saved. Customers are being updated in the background.',
      });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to save fee waiver settings' });
    }
  };

  const handleSave = () => {
    if (!settingsChanged) {
      return;
    }

    const changedLabels = changedFields
      .map((field) => {
        if (field === 'currentDueWaiverDayOfMonthCutoff') {
          const day = settings.currentDueWaiverDayOfMonthCutoff;
          return day === null
            ? `${CUTOFF_LABEL} (removed)`
            : `${CUTOFF_LABEL} (${getOrdinalSuffix(day)} of the month)`;
        }
        return `${WAIVER_META[field].label.toLowerCase()} (${settings[field] ? 'on' : 'off'})`;
      })
      .join(', ');

    const subTitle = overwriteExisting
      ? `Saving will apply ${changedLabels} to all of your customers. Any fee waiver customizations made to specific customers will be overwritten.`
      : `Saving will apply ${changedLabels} to customers that have no fee waiver customizations of their own. Customers you have customized will be left as they are.`;

    dispatch(
      openMessageModal({
        modalType: 'WARN',
        title: overwriteExisting
          ? 'Update fee waivers for all customers?'
          : 'Update fee waivers for customers without customizations?',
        subTitle,
        buttonText: 'Cancel',
        secondButtonText: 'Proceed',
        isSecondButton: true,
        onSecondButtonClick: async () => {
          await performSave();
        },
      })
    );
  };

  const scopeOptions: { value: boolean; label: string; helper: string }[] = [
    {
      value: false,
      label: 'Only customers without customizations',
      helper: 'Customers with their own fee waiver settings are left untouched',
    },
    {
      value: true,
      label: 'All customers',
      helper: 'Overwrites fee waiver customizations made to specific customers',
    },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-900">Credit Card Fee Waivers</h4>
        <p className="text-xs text-gray-400 mt-0.5">
          Turn on a waiver to stop charging credit card fees in that situation. This applies to new
          customers, and to existing customers based on the option you pick below.
        </p>
      </div>

      <div className="flex flex-col gap-2 flex-1">
        {ALL_WAIVER_TOGGLE_FIELDS.map((field) => {
          const { label, description, Icon } = WAIVER_META[field];
          return (
            <div
              key={field}
              className={twMerge(
                'flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3',
                !canEdit && 'opacity-70'
              )}
              data-tooltip-id={!canEdit ? 'no-permission-fee-waiver-tooltip' : undefined}>
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-100">
                  <Icon size={16} color={COLORS.GREY_600} weight="bold" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">{label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                  {field === 'waiveForCurrentDuePayments' &&
                    settings.waiveForCurrentDuePayments && (
                      <div className="mt-3 border-t border-gray-100 pt-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-gray-800">
                              Only until a day of the month
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              For prox terms. Customers lose the waiver after this day, even on
                              invoices that are not yet past due.
                            </p>
                          </div>
                          <DillToggle
                            value={settings.currentDueWaiverDayOfMonthCutoff !== null}
                            onChange={toggleCutoff}
                            isDisabled={!canEdit}
                          />
                        </div>
                        {settings.currentDueWaiverDayOfMonthCutoff !== null && (
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-xs text-gray-600">Paid on or before the</span>
                            <div className="w-24">
                              <DillSelect
                                value={`${settings.currentDueWaiverDayOfMonthCutoff}`}
                                options={DAY_OF_MONTH_OPTIONS}
                                disabled={!canEdit}
                                onChange={(value) =>
                                  setSettings((prev) => ({
                                    ...prev,
                                    currentDueWaiverDayOfMonthCutoff: Number(value),
                                  }))
                                }
                              />
                            </div>
                            <span className="text-xs text-gray-600">of the month</span>
                          </div>
                        )}
                      </div>
                    )}
                </div>
              </div>
              <div className="pt-0.5">
                <DillToggle
                  value={settings[field]}
                  onChange={() => toggleField(field)}
                  isDisabled={!canEdit}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4">
        <p className="text-xs font-medium text-gray-800 mb-2">Apply to</p>
        <div className="flex flex-col gap-2">
          {scopeOptions.map((option) => (
            <div
              key={`${option.value}`}
              role="radio"
              aria-checked={overwriteExisting === option.value}
              aria-disabled={!canEdit}
              tabIndex={!canEdit ? -1 : 0}
              onClick={() => {
                if (canEdit) setOverwriteExisting(option.value);
              }}
              onKeyDown={(e) => {
                if (canEdit && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  setOverwriteExisting(option.value);
                }
              }}
              className={twMerge(
                'flex items-center gap-3 rounded-lg border bg-white px-4 py-3 transition-colors',
                !canEdit
                  ? 'cursor-not-allowed border-gray-200 opacity-70'
                  : 'cursor-pointer border-gray-200 hover:border-gray-300',
                overwriteExisting === option.value &&
                  canEdit &&
                  'border-blue-500 bg-blue-50 hover:border-blue-500'
              )}>
              <AppRadioButton
                isChecked={overwriteExisting === option.value}
                isDisabled={!canEdit}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{option.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{option.helper}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <div data-tooltip-id={!canEdit ? 'no-permission-fee-waiver-tooltip' : undefined}>
          <DillButton
            onClick={handleSave}
            label="Save changes"
            isLoading={saving}
            isDisabled={!canEdit || !settingsChanged}
          />
        </div>
      </div>

      {!canEdit && (
        <ReactTooltip
          id="no-permission-fee-waiver-tooltip"
          place="top"
          variant="dark"
          opacity="100%"
          style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
          You do not have permission to make changes
        </ReactTooltip>
      )}
    </div>
  );
};
